import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Store, Users, UtensilsCrossed, CreditCard, LogOut, ExternalLink, Loader2, Search } from "lucide-react";
import api from "@/lib/api";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useAuth } from "@/context/AuthContext";
import { AdminStripeCard } from "@/components/AdminStripeCard";
import { AdminDeleteAccount } from "@/components/AdminDeleteAccount";
import { AdminPlanButton } from "@/components/AdminPlanButton";
import { AdminInfoRequests } from "@/components/AdminInfoRequests";
import { AdminMarketing } from "@/components/AdminMarketing";
import { AdminDeletedAccounts } from "@/components/AdminDeletedAccounts";
import { AdminSuspendButton } from "@/components/AdminSuspendButton";
import { AdminShowcaseButton } from "@/components/AdminShowcaseButton";
import { AdminTestPlanCard } from "@/components/AdminTestPlanCard";
import { AdminImpersonateButton } from "@/components/AdminImpersonateButton";
import { Input } from "@/components/ui/input";

const PLAN_OPTIONS = [
  { value: "all", label: "Tutti i piani" }, { value: "free", label: "Free (nessun piano)" },
  { value: "basic_monthly", label: "Basic" }, { value: "avanzato_monthly", label: "Avanzato" },
  { value: "pro_monthly", label: "Pro" }, { value: "business_monthly", label: "Business" }, { value: "daily", label: "A Tempo" },
];

const planKey = (r) => {
  const s = r.subscription;
  if (!s || s.status !== "active") return "free";
  return s.lookup_key?.startsWith("daily") ? "daily" : s.lookup_key;
};

export default function AdminPanel() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [restaurants, setRestaurants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [plan, setPlan] = useState("all");

  const q = query.trim().toLowerCase();
  const filtered = restaurants.filter((r) =>
    (plan === "all" || planKey(r) === plan) &&
    (!q || [r.name, r.slug, r.owner_name, r.owner_email].some((v) => (v || "").toLowerCase().includes(q))));

  useEffect(() => {
    (async () => {
      const [s, r] = await Promise.all([api.get("/admin/stats"), api.get("/admin/restaurants")]);
      setStats(s.data);
      setRestaurants(r.data);
      setLoading(false);
    })();
  }, []);

  const doLogout = async () => { await logout(); navigate("/login"); };

  const cards = stats ? [
    { label: "Ristoratori", value: stats.users, icon: Users },
    { label: "Ristoranti", value: stats.restaurants, icon: Store },
    { label: "Piatti totali", value: stats.dishes, icon: UtensilsCrossed },
    { label: "Abbonamenti attivi", value: stats.active_subscriptions, icon: CreditCard },
  ] : [];

  return (
    <div className="min-h-screen bg-[#FAFBFA] text-gray-900">
      <header className="h-20 mo-glass flex items-center justify-between px-6 sticky top-0 z-20">
        <div className="flex items-center gap-4">
          <Logo />
          <span className="text-xs font-semibold uppercase tracking-wider text-[#4a8a56] bg-[#5aa469]/12 px-3 py-1 rounded-full">Admin</span>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-sm text-gray-500 hidden sm:block">{user?.email}</span>
          <Button variant="ghost" onClick={doLogout} className="text-gray-600 hover:bg-gray-100" data-testid="admin-logout"><LogOut className="h-4 w-4 mr-2" />Esci</Button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto p-6 lg:p-8 space-y-8" data-testid="admin-panel">
        <div>
          <h1 className="font-display font-bold text-4xl">Pannello Admin</h1>
          <p className="text-gray-500 mt-1">Gestisci tutti gli account e i ristoranti della piattaforma.</p>
        </div>

        <AdminTestPlanCard />
        <AdminStripeCard />
        <AdminInfoRequests />
        <AdminMarketing />
        <AdminDeletedAccounts />

        {loading ? <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-[#5aa469]" /></div> : (
          <>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              {cards.map((c) => (
                <div key={c.label} className="rounded-xl mo-card p-6" data-testid={`admin-stat-${c.label.toLowerCase().replace(/\s/g, "-")}`}>
                  <div className="flex items-center justify-between">
                    <p className="text-sm text-gray-500">{c.label}</p>
                    <c.icon className="h-5 w-5 text-[#5aa469]" />
                  </div>
                  <p className="text-3xl font-extrabold mt-2 font-display">{c.value}</p>
                </div>
              ))}
            </div>

            <div className="rounded-2xl mo-card overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-100 flex flex-wrap items-center justify-between gap-3">
                <h3 className="font-display font-semibold text-2xl">Ristoranti registrati <span className="text-base text-gray-400 font-sans font-normal">({filtered.length})</span></h3>
                <div className="flex flex-wrap gap-2">
                  <div className="relative">
                    <Search className="h-4 w-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                    <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Cerca nome, email, slug…" className="pl-9 h-10 w-64" data-testid="admin-search" />
                  </div>
                  <select value={plan} onChange={(e) => setPlan(e.target.value)} className="h-10 rounded-md border border-gray-200 bg-white px-3 text-sm" data-testid="admin-plan-filter">
                    {PLAN_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </div>
              </div>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="hover:bg-transparent">
                      <TableHead>Ristorante</TableHead>
                      <TableHead>Proprietario</TableHead>
                      <TableHead>Piano</TableHead>
                      <TableHead className="text-center">Piatti</TableHead>
                      <TableHead className="text-right">Menù</TableHead>
                      <TableHead className="text-right">Azioni</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filtered.map((r) => (
                      <TableRow key={r.id} className="hover:bg-gray-50" data-testid={`admin-resto-${r.id}`}>
                        <TableCell className="font-medium text-gray-900">
                          {r.name}
                          {r.suspended && <span className="ml-2 text-[10px] font-semibold uppercase tracking-wide text-red-700 bg-red-100 px-2 py-0.5 rounded-full" data-testid={`suspended-badge-${r.id}`}>Sospeso</span>}
                        </TableCell>
                        <TableCell className="text-gray-600">
                          <div>{r.owner_name}</div>
                          <div className="text-xs text-gray-400">{r.owner_email}</div>
                        </TableCell>
                        <TableCell>
                          {r.subscription?.status === "active"
                            ? <span className="text-xs font-medium text-[#4a8a56] bg-[#5aa469]/12 px-2.5 py-1 rounded-full">{r.subscription.name}{r.subscription.test_mode ? " · omaggio" : ""}</span>
                            : <span className="text-xs text-gray-400">—</span>}
                        </TableCell>
                        <TableCell className="text-center text-gray-700">{r.dish_count}</TableCell>
                        <TableCell className="text-right">
                          <a href={`/menu/${r.slug}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-[#5aa469] text-sm hover:underline">
                            Apri <ExternalLink className="h-3.5 w-3.5" />
                          </a>
                        </TableCell>
                        <TableCell className="text-right whitespace-nowrap">
                          <AdminImpersonateButton restaurant={r} />
                          <AdminPlanButton restaurant={r} onChanged={(ownerId, subscription) => setRestaurants((prev) => prev.map((x) => (x.owner_id === ownerId ? { ...x, subscription } : x)))} />
                          <AdminShowcaseButton restaurant={r} onToggled={(id, showcase) => setRestaurants((prev) => prev.map((x) => ({ ...x, showcase: x.id === id ? showcase : false })))} />
                          <AdminSuspendButton restaurant={r} onToggled={(id, suspended) => setRestaurants((prev) => prev.map((x) => (x.id === id ? { ...x, suspended } : x)))} />
                          <AdminDeleteAccount restaurant={r} onDeleted={(ownerId) => {
                            setRestaurants((prev) => prev.filter((x) => x.owner_id !== ownerId));
                            setStats((s) => s ? { ...s, users: s.users - 1, restaurants: s.restaurants - 1, dishes: s.dishes - r.dish_count } : s);
                          }} />
                        </TableCell>
                      </TableRow>
                    ))}
                    {filtered.length === 0 && (
                      <TableRow><TableCell colSpan={6} className="text-center text-gray-400 py-10">{restaurants.length ? "Nessun risultato per la ricerca." : "Nessun ristorante registrato."}</TableCell></TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            </div>
          </>
        )}
      </main>
    </div>
  );
}
